import { useEffect, useId, useRef, useState } from "react";
import { Building2, Plus, Search } from "lucide-react";
import { Input } from "@/shared/ui/input.js";
import { cn } from "@/shared/lib/utils.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import { searchClientes } from "../services/reports.service.js";
import { deriveSigla } from "../lib/sigla.js";
import type { Cliente } from "../types.js";

/**
 * Valor del combobox: siempre hay un `nombre`; `clienteId` está presente solo
 * cuando se eligió un cliente EXISTENTE. Sin `clienteId` → nombre nuevo (el
 * backend hará find-or-create al crear el informe).
 */
export interface ClienteValue {
  clienteId?: string;
  nombre: string;
}

interface ClienteComboboxProps {
  value: ClienteValue;
  onChange: (value: ClienteValue) => void;
  disabled?: boolean;
}

const labelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";
const DEBOUNCE_MS = 250;

/** Chip mono de sigla, reutilizado en cada fila y en la opción «crear». */
function SiglaChip({ sigla }: { sigla: string }) {
  return (
    <span className="shrink-0 rounded-[var(--radius-badge)] border border-border bg-secondary/60 px-1.5 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.12em] text-muted-foreground">
      {sigla}
    </span>
  );
}

/**
 * ClienteCombobox — input controlado con autocomplete find-or-create.
 *
 * - Tipea → debounce 250ms → searchClientes(q) → dropdown de coincidencias.
 * - Elegir una coincidencia → onChange({ clienteId, nombre }).
 * - Si no hay match EXACTO, ofrece «Crear «<nombre>»» → onChange({ nombre }) (sin id),
 *   con preview de la sigla derivada.
 * - Teclado: ↑/↓ navega, Enter selecciona el resaltado, Esc cierra.
 */
export function ClienteCombobox({ value, onChange, disabled }: ClienteComboboxProps) {
  const inputId = useId();
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState(value.nombre);
  const [matches, setMatches] = useState<Cliente[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);

  // Mantener el input en sync si el padre resetea el valor externamente.
  useEffect(() => {
    setQuery(value.nombre);
  }, [value.nombre]);

  // Búsqueda con debounce. Se cancela en cada tecla y al desmontar.
  useEffect(() => {
    const q = query.trim();
    if (q.length === 0) {
      setMatches([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    let activo = true;
    const handle = window.setTimeout(() => {
      searchClientes(q)
        .then((data) => {
          if (activo) {
            setMatches(data);
            setHighlight(0);
          }
        })
        .catch((err: unknown) => {
          if (activo) setError(friendlyMessage(err));
        })
        .finally(() => {
          if (activo) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      activo = false;
      window.clearTimeout(handle);
    };
  }, [query]);

  // Cerrar al hacer click afuera.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const trimmed = query.trim();
  const exactMatch = matches.some((c) => c.nombre.toLowerCase() === trimmed.toLowerCase());
  const showCreate = trimmed.length > 0 && !exactMatch;
  // Las opciones navegables: clientes + (opcional) la opción «crear» al final.
  const createIndex = showCreate ? matches.length : -1;
  const optionCount = matches.length + (showCreate ? 1 : 0);

  function selectCliente(c: Cliente) {
    onChange({ clienteId: c.id, nombre: c.nombre });
    setQuery(c.nombre);
    setOpen(false);
  }

  function selectCreate() {
    onChange({ nombre: trimmed });
    setOpen(false);
  }

  function handleInput(next: string) {
    setQuery(next);
    setOpen(true);
    // Tipear invalida cualquier selección previa de cliente existente.
    onChange({ nombre: next });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => (optionCount === 0 ? 0 : (h + 1) % optionCount));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (optionCount === 0 ? 0 : (h - 1 + optionCount) % optionCount));
    } else if (e.key === "Enter") {
      if (!open || optionCount === 0) return;
      e.preventDefault();
      if (highlight === createIndex) {
        selectCreate();
      } else {
        const c = matches[highlight];
        if (c) selectCliente(c);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  const isNew = trimmed.length > 0 && value.clienteId === undefined;

  return (
    <div ref={containerRef} className="relative grid gap-1.5">
      <label htmlFor={inputId} className={labelClass}>
        Cliente
      </label>
      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          placeholder="Busca o escribe un cliente nuevo…"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className="pl-9 font-mono text-sm"
        />
      </div>

      {/* Estado seleccionado: existente (sigla del catálogo) o nuevo (sigla derivada). */}
      {value.clienteId !== undefined ? (
        <p className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-muted-foreground">
          Cliente existente
        </p>
      ) : isNew ? (
        <p className="flex items-center gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-muted-foreground">
          Nuevo cliente · sigla <SiglaChip sigla={deriveSigla(trimmed) || "—"} />
        </p>
      ) : null}

      {open && trimmed.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Clientes"
          className="absolute top-full z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-card py-1 shadow-md"
        >
          {loading && (
            <li className="px-3 py-2 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-muted-foreground">
              Buscando…
            </li>
          )}
          {error && !loading && <li className="px-3 py-2 text-sm text-destructive">{error}</li>}
          {!loading &&
            !error &&
            matches.map((c, i) => (
              <li key={c.id} role="option" aria-selected={highlight === i}>
                <button
                  type="button"
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => selectCliente(c)}
                  className={cn(
                    "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
                    highlight === i
                      ? "bg-secondary text-foreground"
                      : "text-foreground hover:bg-secondary/60",
                  )}
                >
                  <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="flex-1 truncate">{c.nombre}</span>
                  <SiglaChip sigla={c.sigla} />
                </button>
              </li>
            ))}
          {showCreate && !loading && !error && (
            <li role="option" aria-selected={highlight === createIndex}>
              <button
                type="button"
                onMouseEnter={() => setHighlight(createIndex)}
                onClick={selectCreate}
                className={cn(
                  "flex w-full items-center gap-2.5 border-t border-border px-3 py-2 text-left text-sm transition-colors",
                  highlight === createIndex
                    ? "bg-brand-accent/10 text-foreground"
                    : "text-foreground hover:bg-brand-accent/5",
                )}
              >
                <Plus className="size-4 shrink-0 text-brand-accent" aria-hidden />
                <span className="flex-1 truncate">
                  Crear «<span className="font-medium">{trimmed}</span>»
                </span>
                <SiglaChip sigla={deriveSigla(trimmed) || "—"} />
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
